/**
 * Pure date/grouping helpers for the resident Todo Calendar.
 * All functions take dates as arguments — none read the clock, so tests can
 * pin any date. Native Date + Intl only (repo convention; no date-fns).
 */
import type { ResidentTodo } from '@/types/residents/todos';

const pad2 = (n: number): string => String(n).padStart(2, '0');

/** Local calendar date -> 'YYYY-MM-DD'. Never uses toISOString (UTC shift). */
export const toDateKey = (d: Date): string =>
  `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;

/** 'YYYY-MM-DD' -> local midnight Date. */
export const parseDateKey = (key: string): Date => {
  const [year, month, day] = key.split('-').map(Number);
  return new Date(year, month - 1, day);
};

export const addDays = (d: Date, n: number): Date =>
  new Date(d.getFullYear(), d.getMonth(), d.getDate() + n);

export const isSameDay = (a: Date, b: Date): boolean =>
  a.getFullYear() === b.getFullYear() &&
  a.getMonth() === b.getMonth() &&
  a.getDate() === b.getDate();

/** Sunday-start week. */
export const startOfWeek = (d: Date): Date => addDays(d, -d.getDay());

export const getWeekDays = (d: Date): Date[] => {
  const first = startOfWeek(d);
  return Array.from({ length: 7 }, (_, i) => addDays(first, i));
};

/** e.g. "Wed, Aug 5". Locale is pinned to en-US to match the repo's other
 *  formatters (see utils/residents/date.ts) and to keep tests deterministic —
 *  the runtime default would vary by machine. */
export const formatDayHeading = (d: Date): string =>
  d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });

export const groupTodosByDateKey = (
  todos: ResidentTodo[],
): Record<string, ResidentTodo[]> => {
  const groups: Record<string, ResidentTodo[]> = {};
  for (const t of todos) {
    (groups[t.dueDate] ??= []).push(t);
  }
  return groups;
};

/** Alphabetical by title. Completion is deliberately NOT a sort key: a row a
 *  resident ticks keeps its slot instead of dropping to the bottom of the day,
 *  so the list never reshuffles under their finger. */
export const sortTodos = (todos: ResidentTodo[]): ResidentTodo[] =>
  [...todos].sort((a, b) => a.title.localeCompare(b.title));

/** First day (local midnight) of d's month. */
export const startOfMonth = (d: Date): Date =>
  new Date(d.getFullYear(), d.getMonth(), 1);

/** First day of the month `delta` months away. Anchoring to the 1st makes
 *  day-clamping (Jan 31 → "Feb 31") impossible by construction. */
export const addMonthsToStart = (d: Date, delta: 1 | -1): Date =>
  new Date(d.getFullYear(), d.getMonth() + delta, 1);

export const isSameMonth = (a: Date, b: Date): boolean =>
  a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth();

/** e.g. "August 2026". Locale pinned to en-US like the other formatters. */
export const formatMonthLabel = (d: Date): string =>
  d.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });

/** Sunday-start weeks covering d's month, padded with real adjacent-month
 *  days (4–6 rows × 7). Every cell is a local-midnight Date. */
export const getMonthMatrix = (d: Date): Date[][] => {
  const nextMonthFirst = addMonthsToStart(d, 1);
  const weeks: Date[][] = [];
  let cursor = startOfWeek(startOfMonth(d));
  do {
    weeks.push(getWeekDays(cursor));
    cursor = addDays(cursor, 7);
  } while (cursor < nextMonthFirst);
  return weeks;
};

/** Day-cell a11y label, e.g. "Wednesday, August 5, 1 to do". Shared by both
 *  calendars (MonthGrid and the home WeekStrip) so a date reads identically
 *  whichever one a screen-reader user is on.
 *
 *  "N to do", not "N todo(s)": `count` is REMAINING work — both callers pass
 *  incomplete-only counts — so total-phrasing would announce "0 todos" on a
 *  day whose items are merely all ticked off, telling a screen-reader user to
 *  skip a cell that still opens a populated list. It also has no plural to get
 *  wrong at 0 or 1. */
export const formatDayAccessibilityLabel = (d: Date, count: number): string => {
  const datePart = d.toLocaleDateString('en-US', {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
  });
  return `${datePart}, ${count} to do`;
};
